import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import Toast from '../../components/Toast';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tools, setTools] = useState([]);
  const [toast, setToast] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user', discord_id: '' });
  const [permModal, setPermModal] = useState(null);
  const [userPerms, setUserPerms] = useState([]);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteMaxUses, setInviteMaxUses] = useState(1);
  const [inviteUnlimited, setInviteUnlimited] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [invitations, setInvitations] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [u, r, t, inv] = await Promise.all([api.get('/users'), api.get('/roles'), api.get('/tools/all'), api.get('/invitations').catch(() => [])]);
    setUsers(u.filter(x => !x.pending_approval));
    setPendingUsers(u.filter(x => x.pending_approval));
    setRoles(r);
    setTools(t);
    setInvitations(inv);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modal === 'new') {
        await api.post('/users', form);
        setToast({ message: 'Usuario creado', type: 'success' });
      } else {
        await api.put(`/users/${modal}`, form);
        setToast({ message: 'Usuario actualizado', type: 'success' });
      }
      setModal(null);
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const toggle2FA = async (userId, current) => {
    try {
      await api.put(`/users/${userId}/2fa`, { required: !current });
      setToast({ message: '2FA actualizado', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  // Resetea / desactiva el 2FA de un usuario (p. ej. perdió el móvil).
  // Limpia el secreto TOTP y desmarca totp_enabled y totp_required.
  const reset2FA = async (user) => {
    const msg = user.totp_enabled
      ? `¿Desactivar el 2FA de ${user.name}? Se eliminará su secreto y deberá configurarlo de nuevo si vuelve a ser requerido.`
      : `¿Quitar el requisito de 2FA a ${user.name}?`;
    if (!confirm(msg)) return;
    try {
      await api.del(`/users/${user.id}/2fa`);
      setToast({ message: '2FA desactivado', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  // Desbloquea una cuenta bloqueada por intentos fallidos.
  const unlockUser = async (user) => {
    try {
      await api.post(`/users/${user.id}/unlock`);
      setToast({ message: `Cuenta de ${user.name} desbloqueada`, type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  // Genera una contrasena temporal para el usuario y desbloquea la cuenta.
  const resetPassword = async (user) => {
    if (!confirm(`Generar una contrasena temporal para ${user.name}? La actual dejara de funcionar.`)) return;
    try {
      const r = await api.post(`/users/${user.id}/reset-password`);
      setTempPassword({ user: user.name, password: r.temporaryPassword });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const toggleActive = async (userId, current) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !current });
      setToast({ message: 'Estado actualizado', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const openPerms = async (userId) => {
    const perms = await api.get(`/permissions/user/${userId}`);
    setUserPerms(perms);
    setPermModal(userId);
  };

  const savePerms = async () => {
    try {
      await api.put(`/permissions/user/${permModal}`, { toolIds: userPerms });
      setToast({ message: 'Permisos guardados', type: 'success' });
      setPermModal(null);
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const openEdit = (user) => {
    setForm({ name: user.name, email: user.email, password: '', role: user.role, discord_id: user.discord_id || '' });
    setModal(user.id);
  };

  // ─── Invitations ─────────────────────────────────────────
  const generateInvite = async () => {
    try {
      const result = await api.post('/invitations', {
        role: inviteRole,
        maxUses: inviteUnlimited ? null : inviteMaxUses,
      });
      setInviteUrl(result.url);
      setInviteCopied(false);
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.del(`/invitations/${id}`);
      setToast({ message: 'Invitación revocada', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const copyInviteUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const approveUser = async (userId) => {
    try {
      await api.put(`/invitations/approve/${userId}`);
      setToast({ message: 'Usuario aprobado', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const rejectUser = async (userId) => {
    if (!confirm('¿Rechazar esta solicitud? Se eliminará el usuario.')) return;
    try {
      await api.del(`/invitations/reject/${userId}`);
      setToast({ message: 'Solicitud rechazada', type: 'success' });
      loadData();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {tempPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold mb-1">Contrasena temporal</h3>
            <p className="text-xs text-gray-500 mb-3">Para {tempPassword.user}. Se muestra una sola vez. Se le pedira cambiarla al entrar.</p>
            <div className="font-mono text-sm bg-gray-100 rounded-lg p-3 break-all mb-4 select-all">
              {tempPassword.password}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(tempPassword.password); setToast({ message: 'Copiada', type: 'success' }); }}
                className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Copiar</button>
              <button onClick={() => setTempPassword(null)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending approvals ───────────────────────────────── */}
      {pendingUsers.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-gray-700">Pendientes de aprobación</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FFF3D0', color: '#92610E' }}>
              {pendingUsers.length}
            </span>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#FFB800' }}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{u.name}</p>
                    <p className="text-sm text-gray-500">{u.email}</p>
                    <span className="text-xs capitalize bg-gray-100 px-2 py-0.5 rounded mt-1 inline-block">{u.role}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveUser(u.id)}
                      className="text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors"
                      style={{ backgroundColor: '#22c55e' }}
                    >
                      Aprobar
                    </button>
                    <button
                      onClick={() => rejectUser(u.id)}
                      className="text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                      style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}
                    >
                      Rechazar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Header with actions ─────────────────────────────── */}
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-gray-500">{users.length} usuarios</span>
        <div className="flex gap-2">
          <button onClick={() => { setInviteUrl(''); setInviteRole('user'); setInviteMaxUses(1); setInviteUnlimited(false); setInviteModal(true); }}
            className="btn-dark text-sm px-4 py-2 rounded-lg flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Invitar
          </button>
          <button onClick={() => { setForm({ name: '', email: '', password: '', role: 'user', discord_id: '' }); setModal('new'); }}
            className="btn-accent text-sm px-4 py-2 rounded-lg">
            Nuevo usuario
          </button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr className="text-gray-500 text-xs uppercase">
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Rol</th>
              <th className="px-4 py-3 text-center">2FA</th>
              <th className="px-4 py-3 text-center">Activo</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-gray-500">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.role}</td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex items-center gap-2">
                    <button onClick={() => toggle2FA(u.id, u.totp_required)}
                      title={u.totp_enabled ? '2FA configurado por el usuario' : 'Marcar / desmarcar como requerido'}
                      className={`text-xs px-2 py-0.5 rounded-full ${u.totp_required || u.totp_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.totp_enabled ? 'Activo' : u.totp_required ? 'Requerido' : 'No'}
                    </button>
                    {(u.totp_enabled || u.totp_required) && (
                      <button onClick={() => reset2FA(u)}
                        title="Desactivar 2FA (p. ej. el usuario perdió el dispositivo)"
                        className="text-xs font-medium hover:underline" style={{ color: '#dc2626' }}>
                        Desactivar
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleActive(u.id, u.is_active)}
                    className="relative w-9 h-5 rounded-full transition-colors"
                    style={{ backgroundColor: u.is_active ? '#22c55e' : '#d1d5db' }}>
                    <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                      style={{ left: u.is_active ? '18px' : '2px' }} />
                  </button>
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  {u.locked_until && (
                    <button onClick={() => unlockUser(u)}
                      title="Cuenta bloqueada por intentos fallidos. Desbloquear ahora."
                      className="text-xs font-semibold hover:underline" style={{ color: '#dc2626' }}>
                      Desbloquear
                    </button>
                  )}
                  <button onClick={() => resetPassword(u)}
                    title="Generar contrasena temporal y desbloquear"
                    className="text-xs font-medium hover:underline" style={{ color: '#7c3aed' }}>
                    Contrasena
                  </button>
                  <button onClick={() => openPerms(u.id)} className="text-xs font-medium hover:underline" style={{ color: '#3b82f6' }}>Permisos</button>
                  <button onClick={() => openEdit(u)} className="text-xs font-medium hover:underline" style={{ color: '#FFB800' }}>Editar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {users.map(u => (
          <div key={u.id} className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium">{u.name}</p>
                <p className="text-sm text-gray-500">{u.email}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {u.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
              <span className="capitalize bg-gray-100 px-2 py-0.5 rounded">{u.role}</span>
              {u.totp_enabled && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">2FA</span>}
              {u.locked_until && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">Bloqueado</span>}
            </div>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => openPerms(u.id)} className="text-xs font-medium" style={{ color: '#3b82f6' }}>Permisos</button>
              <button onClick={() => openEdit(u)} className="text-xs font-medium" style={{ color: '#FFB800' }}>Editar</button>
              <button onClick={() => toggleActive(u.id, u.is_active)} className="text-xs text-gray-400">
                {u.is_active ? 'Desactivar' : 'Activar'}
              </button>
              <button onClick={() => resetPassword(u)} className="text-xs font-medium" style={{ color: '#7c3aed' }}>
                Contrasena
              </button>
              {u.locked_until && (
                <button onClick={() => unlockUser(u)} className="text-xs font-semibold" style={{ color: '#dc2626' }}>
                  Desbloquear
                </button>
              )}
              {(u.totp_enabled || u.totp_required) && (
                <button onClick={() => reset2FA(u)} className="text-xs font-medium" style={{ color: '#dc2626' }}>
                  Resetear 2FA
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* User form modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">{modal === 'new' ? 'Nuevo usuario' : 'Editar usuario'}</h3>
            <form onSubmit={handleSave} className="space-y-3">
              <input type="text" placeholder="Nombre" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2" required />
              <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2" required />
              <input type="password" placeholder={modal === 'new' ? 'Contraseña' : 'Nueva contraseña (vacío = sin cambio)'}
                value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2" {...(modal === 'new' ? { required: true } : {})} />
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2">
                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
              {modal !== 'new' && (
                <div>
                  <input type="text" inputMode="numeric" placeholder="ID de Discord (opcional)" value={form.discord_id}
                    onChange={e => setForm({...form, discord_id: e.target.value.replace(/[^0-9]/g, '')})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2" />
                  <p className="text-xs text-gray-400 mt-1">Para que el bot le mencione en #general cuando entre en el podio de Montaje.</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button type="submit" className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Guardar</button>
                <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions modal */}
      {permModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPermModal(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">Permisos de herramientas</h3>
            <p className="text-sm text-gray-500 mb-3">Selecciona las herramientas a las que tendrá acceso:</p>
            <div className="space-y-2 mb-4">
              {tools.map(t => (
                <label key={t.id} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={userPerms.includes(t.id)}
                    onChange={e => {
                      if (e.target.checked) setUserPerms([...userPerms, t.id]);
                      else setUserPerms(userPerms.filter(id => id !== t.id));
                    }} className="rounded border-gray-300" />
                  {t.name}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={savePerms} className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Guardar</button>
              <button onClick={() => setPermModal(null)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setInviteModal(false); setInviteUrl(''); }}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">Invitar al equipo</h3>
            <p className="text-sm text-gray-500 mb-4">Genera un enlace de invitación válido 24 horas.</p>

            {!inviteUrl ? (
              <>
                <div className="space-y-4 mb-5">
                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rol por defecto</label>
                    <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2">
                      {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </div>

                  {/* Max uses */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número de usos</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1" max="100"
                        value={inviteMaxUses}
                        disabled={inviteUnlimited}
                        onChange={e => setInviteMaxUses(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                        className="w-24 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400"
                      />
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={inviteUnlimited}
                          onChange={e => setInviteUnlimited(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        Ilimitado
                      </label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {inviteUnlimited ? 'El enlace no se desactivará por usos.' : `El enlace se desactivará tras ${inviteMaxUses} registro${inviteMaxUses > 1 ? 's' : ''}.`}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={generateInvite} className="btn-accent flex-1 py-2.5 rounded-lg text-sm">Generar enlace</button>
                  <button onClick={() => setInviteModal(false)} className="btn-ghost flex-1 py-2.5 rounded-lg text-sm">Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enlace de invitación</label>
                  <div className="flex gap-2">
                    <input type="text" value={inviteUrl} readOnly
                      className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600" />
                    <button onClick={copyInviteUrl}
                      className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                      style={{ backgroundColor: inviteCopied ? '#e8f5e9' : '#FFB800', color: inviteCopied ? '#2e7d32' : '#0f1117' }}>
                      {inviteCopied ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-4">Al registrarse, aparecerá en "Pendientes de aprobación" para que lo valides.</p>
                <button onClick={() => { setInviteUrl(''); }} className="text-sm font-medium w-full py-2 rounded-lg mb-2"
                  style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>
                  Generar otro enlace
                </button>
                <button onClick={() => { setInviteModal(false); setInviteUrl(''); }} className="btn-ghost w-full py-2.5 rounded-lg text-sm">Cerrar</button>
              </>
            )}

            {/* Active invitations list */}
            {invitations.filter(i => !i.used && new Date(i.expires_at) > new Date()).length > 0 && (
              <div className="mt-5 pt-4" style={{ borderTop: '1px solid #f3f4f6' }}>
                <p className="text-xs font-medium text-gray-500 mb-2">ENLACES ACTIVOS</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {invitations.filter(i => !i.used && new Date(i.expires_at) > new Date()).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="flex items-center gap-2">
                        <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded capitalize">{inv.default_role}</span>
                        <span className="font-mono font-medium" style={{ color: '#FFB800' }}>
                          {inv.current_uses ?? 0} / {inv.max_uses === null ? '∞' : inv.max_uses} usos
                        </span>
                      </div>
                      <button onClick={() => revokeInvite(inv.id)} className="text-red-400 hover:text-red-600 font-medium">
                        Revocar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
